import moment from 'moment';
import type { Moment } from 'moment';
import SparkMD5 from 'spark-md5';

export function fixedZero(value: string | number): string | number {
  return Number(value) < 10 ? `0${value}` : value;
}

export type TimeDistance = readonly [Moment, Moment];

export function getTimeDistance(
  type: 'today' | 'week' | 'month' | 'year',
): TimeDistance | undefined {
  const now = new Date();
  const oneDay = 1000 * 60 * 60 * 24;

  if (type === 'today') {
    now.setHours(0, 0, 0);
    return [moment(now), moment(now.getTime() + oneDay - 1000)];
  }

  if (type === 'week') {
    let day = now.getDay();
    now.setHours(0, 0, 0);
    day = day === 0 ? 6 : day - 1;
    const beginTime = now.getTime() - day * oneDay;
    return [moment(beginTime), moment(beginTime + 7 * oneDay - 1000)];
  }

  if (type === 'month') {
    const year = now.getFullYear();
    const month = now.getMonth();
    const nextDate = moment(now).add(1, 'months');
    return [
      moment(`${year}-${fixedZero(month + 1)}-01 00:00:00`),
      moment(
        moment(`${nextDate.year()}-${fixedZero(nextDate.month() + 1)}-01 00:00:00`).valueOf() -
          1000,
      ),
    ];
  }

  if (type === 'year') {
    const year = now.getFullYear();
    return [moment(`${year}-01-01 00:00:00`), moment(`${year}-12-31 23:59:59`)];
  }

  return undefined;
}

export function digitUppercase(value: number): string {
  const fraction = ['角', '分'] as const;
  const digit = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'] as const;
  const unit = [
    ['元', '万', '亿'],
    ['', '拾', '佰', '仟'],
  ] as const;
  let number = Math.abs(value);
  let result = '';
  fraction.forEach((item, index) => {
    result += `${digit[Math.floor(number * 10 * 10 ** index) % 10] ?? ''}${item}`.replace(
      /零./u,
      '',
    );
  });
  result ||= '整';
  number = Math.floor(number);
  for (let index = 0; index < unit[0].length && number > 0; index += 1) {
    let part = '';
    for (let place = 0; place < unit[1].length && number > 0; place += 1) {
      part = `${digit[number % 10] ?? ''}${unit[1][place] ?? ''}${part}`;
      number = Math.floor(number / 10);
    }
    result = `${part.replace(/(零.)*零$/u, '').replace(/^$/u, '零')}${unit[0][index] ?? ''}${result}`;
  }
  return result
    .replace(/(零.)*零元/u, '元')
    .replace(/(零.)+/gu, '零')
    .replace(/^整$/u, '零元整');
}

const ipv4Pattern =
  /^(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/u;
const ipv6Pattern = /^(?:[\dA-Fa-f]{1,4}:){7}[\dA-Fa-f]{1,4}$/u;

export function isIPV4ValidIP(ip: string): boolean {
  return ipv4Pattern.test(ip);
}

export function isIPV6ValidIP(ip: string): boolean {
  return ipv6Pattern.test(ip);
}

// Preserve the legacy conjunction, including its effectively-always-false behavior.
export function isIPValid(ip: string): boolean {
  return isIPV6ValidIP(ip) && isIPV4ValidIP(ip);
}

export function readAsBinaryString(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onerror = () => reject(reader.error ?? new Error('读取文件失败'));
    reader.onload = (event) => {
      const binary = event.target?.result;
      resolve(SparkMD5.hashBinary(typeof binary === 'string' ? binary : ''));
    };
  });
}
